import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
} from "@mui/material";
import React, { useEffect, useRef } from "react";
import { releaseApi } from "../services/api/release";
import { Release } from "../models/release";

function useInterval(callback: () => void, delay: number) {
  const savedCallback = useRef<() => void>(null);

  // Remember the latest callback.
  useEffect(() => {
    savedCallback.current = callback;
  }, [callback]);

  // Set up the interval.
  useEffect(() => {
    function tick() {
      savedCallback.current!();
    }
    if (delay !== null) {
      let id = setInterval(tick, delay);
      return () => clearInterval(id);
    }
  }, [delay]);
}

export const ReleaseChecker = () => {
  const [open, setOpen] = React.useState(false);
  const [initialRelease, setInitialRelease] = React.useState<Release | null>(
    null,
  );
  const [latestRelease, setLatestRelease] = React.useState<Release | null>(
    null,
  );
  const isMounted = useRef(true);

  const checkForNewRelease = async () => {
    if (open) return;

    try {
      const rel = await releaseApi.get();

      if (!initialRelease) {
        if (isMounted.current) {
          setInitialRelease(rel);
        }
        return;
      }

      if (rel.version !== initialRelease.version) {
        if (isMounted.current) {
          setLatestRelease(rel);
          setOpen(true);
        }
      }
    } catch (error) {
      // suppress
    }
  };

  useInterval(() => {
    checkForNewRelease();
  }, 60000);

  useEffect(() => {
    isMounted.current = true;
    checkForNewRelease();
    return () => {
      isMounted.current = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleClose = () => {
    setInitialRelease(latestRelease);
    setOpen(false);
  };

  const handleReload = () => {
    window.location.reload();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      aria-labelledby="alert-dialog-title"
      aria-describedby="alert-dialog-description"
    >
      <DialogTitle id="alert-dialog-title">SeedLeaf Updated</DialogTitle>
      <DialogContent>
        <DialogContentText id="alert-dialog-description">
          A new version of SeedLeaf has been released. Please refresh your
          browser or click the button below to update to the latest version. You
          can cancel the update if you are in the middle of something important,
          but we recommend updating as soon as possible to avoid any issues.
        </DialogContentText>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>Cancel</Button>
        <Button onClick={handleReload} autoFocus>
          Reload
        </Button>
      </DialogActions>
    </Dialog>
  );
};
